import React, { FC, useState, useEffect } from 'react';
import { Sidebar } from '../../components/Sidebar/Sidebar';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { sessionStore } from 'src/store/sessionStore';
import { useGetOnboardingState } from 'src/api/mutations/useGetOnboardingState';
import { auth } from 'src/config/firebase';
import { updateTrainerProfile } from 'src/api/mutations/useUpdateTrainerProfile';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import { useIsDesktop } from 'src/hooks/useIsDesktop';
import './Dashboard.scss';
import { ReactComponent as HamburgerIcon } from '../../../../assets/images/onboarding-trainer/dashboard/hamburger.svg';
import { ReactComponent as LogoIcon } from '../../../../assets/images/onboarding-trainer/logo.svg';

export const Dashboard: FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const isLoggedIn = sessionStore((state) => state.isLoggedIn);
  const currentUser = sessionStore((state) => state.currentUser);

  // Fetch latest profile data in background if logged in
  useGetOnboardingState(isLoggedIn ? currentUser?.uid : undefined);

  // Fetch program generator master data in the background and sync to UI store automatically
  useGetProgramMasterData();

  const location = useLocation();

  useEffect(() => {
    // Auto-collapse sidebar on program generator and generated routes
    if (
      location.pathname.includes('/dashboard/program-generator') ||
      location.pathname.includes('/dashboard/program-generated') ||
      location.pathname.match(/\/dashboard\/clients\/[^/]+/)
    ) {
      setIsSidebarCollapsed(true);
    } else {
      setIsSidebarCollapsed(false);
    }

    // Scroll dashboard content to top on route changes
    const scrollableMain = document.getElementById('scrollableDashboardMain');
    if (scrollableMain) {
      scrollableMain.scrollTop = 0;
    }
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [location.pathname]);

  useEffect(() => {
    // Automatically reload Firebase Auth user to catch external email verifications
    if (auth.currentUser && currentUser) {
      auth.currentUser
        .reload()
        .then(() => {
          const authEmail = auth.currentUser?.email;
          if (authEmail && authEmail !== currentUser.email) {
            // If the email was verified and updated in Firebase Auth, sync it to our backend & store
            sessionStore.getState().updateProfile({ email: authEmail });
            updateTrainerProfile({
              uid: auth.currentUser!.uid,
              data: { email: authEmail },
            }).catch(console.error);
          }
        })
        .catch(console.error);
    }
  }, [currentUser]);

  const isDesktop = useIsDesktop(768);
  const isDesktopMessages = isDesktop && location.pathname.includes('/dashboard/messages');
  const isMobileChatActive =
    !isDesktop && Boolean(location.pathname.match(/\/dashboard\/messages\/[^/]+/));
  const isChatActive = isDesktopMessages || isMobileChatActive;

  useEffect(() => {
    if (isChatActive) {
      document.documentElement.classList.add('chat-active-page');
      document.body.classList.add('chat-active-page');
    } else {
      document.documentElement.classList.remove('chat-active-page');
      document.body.classList.remove('chat-active-page');
    }
    return () => {
      document.documentElement.classList.remove('chat-active-page');
      document.body.classList.remove('chat-active-page');
    };
  }, [isChatActive]);

  if (!isLoggedIn) {
    return <Navigate to="/onboarding-trainer/signup" replace />;
  }

  return (
    <div
      className={`onboarding-dashboard-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${
        isChatActive ? 'chat-view-active-layout' : ''
      }`}
    >
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div
        className={`dashboard-main ${isChatActive ? 'chat-view-active' : ''}`}
        id="scrollableDashboardMain"
      >
        {/* Mobile Header */}
        <div className="mobile-header">
          <button className="hamburger-btn" onClick={() => setIsSidebarOpen(true)}>
            {<HamburgerIcon />}
          </button>
          <div
            className="mobile-logo"
            onClick={() => setIsSidebarOpen(true)}
            style={{ cursor: 'pointer' }}
          >
            {<LogoIcon />}
          </div>
        </div>

        {/* Content Container */}
        <div className={`dashboard-content-wrapper ${isChatActive ? 'chat-content-wrapper' : ''}`}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};
